import React, { useState, useRef, useEffect } from 'react';
import { Maximize2, X } from 'lucide-react';
import './MobileSummaryTable.css';

export const MobileSummaryTable = ({ title, columns, data, accentColor = '#f97316' }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const tableScrollRef = useRef(null);
  const fullscreenScrollRef = useRef(null);

  // Lock body scroll when fullscreen is open, preserving scroll position
  useEffect(() => {
    if (isFullscreen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    };
  }, [isFullscreen]);

  // Track touch movement to distinguish taps from scrolls on the overlay
  const touchStartRef = useRef(null);
  const handleOverlayTouchStart = (e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const handleOverlayClick = (e) => {
    // Only close if the touch didn't move (i.e., it was a tap, not a scroll)
    if (touchStartRef.current) {
      const threshold = 10;
      const touch = touchStartRef.current;
      const dx = Math.abs(e.clientX - touch.x);
      const dy = Math.abs(e.clientY - touch.y);
      if (dx > threshold || dy > threshold) {
        touchStartRef.current = null;
        return;
      }
    }
    touchStartRef.current = null;
    setIsFullscreen(false);
  };

  if (!data || data.length === 0) return null;

  const renderTable = (scrollRef, isFullscreenMode) => (
    <div
      className={`mst-scroll-container ${isFullscreenMode ? 'mst-scroll-fullscreen' : ''}`}
      ref={scrollRef}
    >
      <table className="mst-table">
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th
                key={col.key}
                className={i === 0 ? 'mst-sticky-col mst-sticky-header' : 'mst-header-cell'}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => {
            const isTotal = row.isTotal;
            return (
              <tr key={rowIndex} className={isTotal ? 'mst-total-row' : ''}>
                {columns.map((col, colIndex) => {
                  const rawValue = row[col.key];
                  const displayValue = col.render ? col.render(rawValue, row) : rawValue;
                  return (
                    <td
                      key={col.key}
                      className={colIndex === 0 ? 'mst-sticky-col mst-location-cell' : 'mst-data-cell'}
                    >
                      {displayValue}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      {/* Inline card view */}
      <div className="mst-card">
        <div className="mst-card-header">
          <h3 className="mst-card-title">{title}</h3>
          <button
            className="mst-expand-button"
            onClick={() => setIsFullscreen(true)}
            aria-label="View fullscreen"
          >
            <Maximize2 size={16} />
          </button>
        </div>
        <div className="mst-scroll-hint">Scroll to view &middot; Tap expand for full view</div>
        {renderTable(tableScrollRef, false)}
      </div>

      {/* Fullscreen modal */}
      {isFullscreen && (
        <div className="mst-fullscreen-overlay" onTouchStart={handleOverlayTouchStart} onClick={handleOverlayClick}>
          <div className="mst-fullscreen-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mst-fullscreen-header">
              <h3 className="mst-fullscreen-title">{title}</h3>
              <button
                className="mst-close-button"
                onClick={() => setIsFullscreen(false)}
                aria-label="Close fullscreen"
              >
                <X size={20} />
              </button>
            </div>
            <div className="mst-fullscreen-body">
              {renderTable(fullscreenScrollRef, true)}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
